import { useCallback } from 'react';
import { parseAsBoolean, useQueryState } from 'nuqs';

/**
 * The query-string key carrying the Labs fullscreen flag. nuqs' boolean
 * parser serializes `true` as the literal `"true"` (and only parses that
 * literal back), so the canonical deep-link is `?fullscreen=true`.
 */
export const LABS_FULLSCREEN_PARAM = 'fullscreen';

/**
 * Labs fullscreen URL state, owned by {@link LabsShell}. The shell feeds
 * this into the shared `LabsFullscreenContext` (from `@reflex/ui`) so game
 * pages can render a toggle in their own header without touching the URL.
 *
 * The flag is written with `history: 'replace'`: fullscreen is a transient
 * view mode owned by the game's header toggle and `esc`, not a navigable
 * destination the browser Back button should land on. The shell gates the
 * whole feature on the active route's `fullscreen` manifest flag (read
 * via `useMatches`), so `?fullscreen=true` only does anything on routes
 * that opted in — a stale param on a non-game page is cleared (see
 * `LabsShell`).
 */
export function useLabsFullscreenState(): {
  fullscreen: boolean;
  setFullscreen: (next: boolean) => void;
  toggleFullscreen: () => void;
} {
  const [fullscreen, setFullscreenParam] = useQueryState(
    LABS_FULLSCREEN_PARAM,
    parseAsBoolean.withDefault(false),
  );

  const setFullscreen = useCallback(
    (next: boolean) => {
      void setFullscreenParam(next ? true : null, { history: 'replace' });
    },
    [setFullscreenParam],
  );

  const toggleFullscreen = useCallback(() => {
    setFullscreen(!fullscreen);
  }, [fullscreen, setFullscreen]);

  return { fullscreen, setFullscreen, toggleFullscreen };
}
